type Listener = (state: { active: boolean }) => void;

// PDF export captures each page inside an iframe. The axios interceptor that
// emits these events runs in the iframe's window context, so without a
// cross-window bridge the parent dialog never learns that requests are being
// throttled. We relay every transition to `window.parent` via postMessage and
// the parent's PdfExportProvider re-emits them locally.
export const THROTTLE_MESSAGE_KIND = 'pdfExport:throttle';

// Body attribute set on the iframe's document while any throttle retry is in
// flight. The PDF export pipeline's `waitForReadiness` reads this and defers
// its 30s stall verdict so a long Retry-After wait isn't mistaken for a hang.
export const THROTTLE_ACTIVE_BODY_ATTR = 'data-export-throttle-active';

export interface ThrottleMessage {
  kind: typeof THROTTLE_MESSAGE_KIND;
  active: boolean;
}

const listeners = new Set<Listener>();
let activeCount = 0;

const updateBodyAttribute = (active: boolean) => {
  if (typeof document === 'undefined' || !document.body) {
    return;
  }
  if (active) {
    document.body.setAttribute(THROTTLE_ACTIVE_BODY_ATTR, '1');
  } else {
    document.body.removeAttribute(THROTTLE_ACTIVE_BODY_ATTR);
  }
};

const isInsideIframe = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.parent !== window;
  } catch {
    return false;
  }
};

const postToParent = (active: boolean) => {
  if (!isInsideIframe()) {
    return;
  }
  try {
    const message: ThrottleMessage = { kind: THROTTLE_MESSAGE_KIND, active };
    window.parent.postMessage(message, window.location.origin);
  } catch {
    // Cross-origin parents reject postMessage with a thrown DOMException —
    // safe to ignore, the iframe-local subscribers still get notified.
  }
};

const notify = () => {
  const active = activeCount > 0;
  listeners.forEach(listener => listener({ active }));
};

export const subscribeThrottleEvents = (listener: Listener): (() => void) => {
  listeners.add(listener);
  listener({ active: activeCount > 0 });
  return () => {
    listeners.delete(listener);
  };
};

export const emitThrottleStart = () => {
  const wasActive = activeCount > 0;
  activeCount += 1;
  notify();
  if (!wasActive) {
    updateBodyAttribute(true);
    postToParent(true);
  }
};

export const emitThrottleEnd = () => {
  activeCount = Math.max(0, activeCount - 1);
  notify();
  if (activeCount === 0) {
    updateBodyAttribute(false);
    postToParent(false);
  }
};

export const isThrottleActive = (): boolean => activeCount > 0;

// Manually clear local state when starting a fresh PDF export run. Capture
// iframes that are torn down mid-retry may never send their final "false"
// message, leaving a stuck active count; resetting at the start of each run
// prevents the banner from leaking across exports.
export const resetThrottleState = () => {
  const wasActive = activeCount > 0;
  activeCount = 0;
  if (wasActive) {
    updateBodyAttribute(false);
    notify();
  }
};
